import React from 'react';
import './ClienteRutaCard.scss';
import { sendMessageTelegram } from '../../../views/monitoreo/ServiciosEnVivo/integracion';
import Swal from 'sweetalert2';

const ClienteRutaCard = ({ cliente, rutas, onCardClick, onRutaClick, turno }) => {
    // Calcular si todas las rutas están completadas
    const todasCompletadas = rutas.every(ruta => ruta.completada);
    const rutasCompletadas = rutas.filter(r => r.completada).length;
    const rutasPendientes = rutas.length - rutasCompletadas;

    // Obtener el nombre y ID del cliente (puede venir como string o array)
    const nombreCliente = Array.isArray(cliente) ? (cliente[0]?.trim() || '') : (cliente || '');
    const clienteId = Array.isArray(cliente) ? (cliente[1] || '') : '';

    // Detectar zona de las rutas (todas deberían tener la misma zona)
    const zona = rutas.length > 0 && rutas[0].zona ? rutas[0].zona.toLowerCase() : '';
    
    // Mapeo de colores por zona
    const coloresPorZona = {
        'a': 'zona-azul',
        'b': 'zona-verde',
        'c': 'zona-naranja'
    };
    
    const claseZona = coloresPorZona[zona] || '';

    // Función para copiar información al portapapeles
    const handleCopyInfo = async (e) => {
        e.stopPropagation();

        // Si hay rutas pendientes, mostrar alerta de confirmación
        if (!todasCompletadas) {
            const result = await Swal.fire({
                icon: 'warning',
                title: '⚠️ Rutas Pendientes',
                html: `
                    <p>Este turno aún tiene <b>${rutasPendientes} ruta(s) pendiente(s)</b>.</p>
                    <p>¿Deseas enviar el mensaje de todas formas?</p>
                `,
                showCancelButton: true,
                confirmButtonText: '📤 Sí, Mandar',
                cancelButtonText: '❌ Cancelar',
                confirmButtonColor: '#667eea',
                cancelButtonColor: '#d33',
                reverseButtons: true
            });

            // Si el usuario cancela, no hacer nada
            if (!result.isConfirmed) {
                return;
            }
        }

        // Función auxiliar para formatear rutas
        const formatearRuta = (ruta, index) => {
            const statusIcon = ruta.completada ? '✅' : '❌';
            
            // Procesar múltiples incidencias separadas por "|"
            let incidenciaTexto = '';
            if (ruta.incidencia && ruta.incidencia !== 'SIN INCIDENCIAS') {
                const incidencias = ruta.incidencia.split('|').map(inc => inc.trim());
                const latitudes = (ruta.latitud || ruta.lat_incidencia || '').toString().split('|').map(lat => lat.trim());
                const longitudes = (ruta.longitud || ruta.lon_incidencia || '').toString().split('|').map(lon => lon.trim());
                
                incidenciaTexto = incidencias.map((inc, idx) => {
                    const lat = latitudes[idx] || '20.5000';
                    const lon = longitudes[idx] || '-103.2520';
                    return `   ⚠️ Incidencia ${idx + 1}: <b>${inc}</b>\n   📍<a href="https://www.google.com/maps/search/?api=1&query=${lat},${lon}" target="_blank" rel="noopener noreferrer"> Ver ubicación </a>\n`;
                }).join('');
            }
            
            return (
                `<b>${ruta.nombre}</b>\n` +
                `   ${statusIcon} Estado: ${ruta.completada ? '<b>COMPLETADA</b>' : '<b>PENDIENTE</b>'}\n` +
                `   🚍 Unidad: <i>${ruta.unidad || 'Por asignar'}</i>\n` +
                `   👥 ocupación: <i>${ruta.ocupacion || '0'}</i>\n` +
                `   ⏳ Arranque: <i>${ruta.horaArranque || 'N/A'}</i>\n` +
                `   📝 Bitacora: <i>${ruta.bitacora || 'N/A'}</i>\n` +
                `   🕕 Arribo: <i>${ruta.arribo || 'N/A'}</i>\n` +
                `   📌 Estatus: <i>${ruta.estatus || 'SIN ESTADO'}</i>\n` +
                incidenciaTexto
            );
        };

        // Header con información general
        const bitacoras = rutas.map(r => r.bitacora || 'N/A').filter((v, i, a) => a.indexOf(v) === i).join(', ');
        
        const headerMensaje =
            `🚍 <b>MONITOREO DE SERVICIOS EN VIVO</b>\n` +
            `━━━━━━━━━━━━━━━━━━━━━━\n` +
            `📊 <b>RESUMEN</b>\n` +
            `🕕 Turno: <b>${turno}</b>\n` +
            `✅ Completadas: ${rutasCompletadas}/${rutas.length}\n` +
            `⏳ Pendientes: ${rutasPendientes}\n` +
            `\n` +
            `━━━━━━━━━━━━━━━━━━━━━━\n` +
            `🚌 <b>DETALLE DE RUTAS</b>\n` +
            `━━━━━━━━━━━━━━━━━━━━━━\n\n`;

        // Dividir rutas en grupos de 6
        const RUTAS_POR_MENSAJE = 6;
        const gruposDeRutas = [];
        
        for (let i = 0; i < rutas.length; i += RUTAS_POR_MENSAJE) {
            gruposDeRutas.push(rutas.slice(i, i + RUTAS_POR_MENSAJE));
        }

        // Preparar mensajes
        const mensajes = [];
        
        // Si hay 6 rutas o menos, enviar todo en un solo mensaje
        if (rutas.length <= RUTAS_POR_MENSAJE) {
            const textoRutas = rutas.map(formatearRuta).join('\n');
            const progreso = `\n━━━━━━━━━━━━━━━━━━━━━━\n📈 <b>PROGRESO:</b> ${rutasCompletadas}/${rutas.length} (${Math.round((rutasCompletadas / rutas.length) * 100)}%)`;
            mensajes.push(headerMensaje + textoRutas + progreso);
        } else {
            // Primer mensaje: header + primer grupo de rutas
            const primerGrupo = gruposDeRutas[0].map(formatearRuta).join('\n');
            mensajes.push(headerMensaje + primerGrupo);
            
            // Mensajes siguientes: solo rutas
            for (let i = 1; i < gruposDeRutas.length; i++) {
                const textoRutas = gruposDeRutas[i].map(formatearRuta).join('\n');
                const esUltimoGrupo = i === gruposDeRutas.length - 1;
                
                if (esUltimoGrupo) {
                    // Último mensaje incluye el progreso
                    const progreso = `\n━━━━━━━━━━━━━━━━━━━━━━\n📈 <b>PROGRESO:</b> ${rutasCompletadas}/${rutas.length} (${Math.round((rutasCompletadas / rutas.length) * 100)}%)`;
                    mensajes.push(textoRutas + progreso);
                } else {
                    mensajes.push(textoRutas);
                }
            }
        }

        // Copiar primer mensaje al portapapeles
        navigator.clipboard.writeText(mensajes[0]).then(() => {
            // Mostrar feedback visual
            const btn = e.currentTarget;
            if (!btn) return;

            const originalText = btn.textContent;
            btn.textContent = '¡Copiado!';
            btn.classList.add('copied');

            setTimeout(() => {
                if (btn && btn.textContent === '¡Copiado!') {
                    btn.textContent = originalText;
                    btn.classList.remove('copied');
                }
            }, 1500);

        }).catch((err) => {
            console.error('Error al copiar:', err);
        });

        // Enviar mensajes secuencialmente
        try {
            for (let i = 0; i < mensajes.length; i++) {
                await sendMessageTelegram(clienteId, mensajes[i]);
                
                // Pequeña pausa entre mensajes para evitar rate limiting
                if (i < mensajes.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }

            Swal.fire({
                icon: 'success',
                title: mensajes.length > 1 
                    ? `${mensajes.length} mensajes enviados correctamente`
                    : 'Mensaje enviado correctamente',
                showConfirmButton: false,
                timer: 1500
            });
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Error al enviar el mensaje',
                text: err.message || 'Ocurrió un error inesperado',
            });
        }
    };

    return (
        <div
            className={`cliente-ruta-card `}
            onClick={onCardClick}
        >
            {/* Header con turno e indicador general */}
            <div className="card-header">
                <div className="header-content">
                    {/* {turno && <span className="label-turno">TURNO: {turno}</span>} */}
                    <h3 className="turno-titulo">TURNO: {turno || 'SERVICIOS'}</h3>
                   
                </div>
                <div className="header-status">
                    <div
                        className={`indicador-general ${todasCompletadas ? 'completado' : 'pendiente'}`}
                        title={todasCompletadas ? 'Todas las rutas completadas' : 'Rutas pendientes'}
                    >
                        <div className="indicador-circle"></div>
                    </div>
                    <span className={`estado-texto ${todasCompletadas ? 'completado' : 'pendiente'}`}>
                        {todasCompletadas ? 'COMPLETO' : 'PENDIENTE'}
                    </span>
                </div>
            </div>

            {/* Resumen rápido */}
            <div className="resumen-rapido">
                <div className="resumen-item llegaron">
                    <span className="resumen-numero">{rutasCompletadas}</span>
                    <span className="resumen-label">COMPLETADOS</span>
                </div>
                <div className="resumen-item pendientes">
                    <span className="resumen-numero">{rutasPendientes}</span>
                    <span className="resumen-label">PENDIENTES</span>
                </div>
            </div>

            {/* Lista de rutas */}
            <div className="rutas-lista">
                {rutas.map((ruta, index) => (
                    <div
                        key={ruta.bitacora || index}
                        className={`ruta-item status-${ruta.estatusId || 5} clickeable`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onRutaClick) {
                                onRutaClick(ruta, nombreCliente, clienteId);
                            }
                        }}
                        title="Click para ver el mapa de la ruta"
                    >
                        <div
                            className={`punto-arribo ${ruta.completada ? 'llegado' : 'pendiente'}`}
                        ></div>
                        <div className="ruta-info">
                            <span className="ruta-nombre" title={ruta.nombre}>{ruta.nombre}</span>
                            <div className="ruta-detalles">
                                <span className="ruta-unidad">🚐 Unidad {ruta.unidad || 'Por asignar'}</span>
                                <span className="ruta-aforo">Ocupación👥 {ruta.ocupacion || '0'}</span>
                                <span className="ruta-horario">Inicio: {ruta.horaArranque || 'N/A'}</span>
                                <span className="ruta-arribo">Fin: {ruta.arribo || 'N/A'}</span>
                                <span className={`ruta-estatus status-${ruta.estatusId || 5}`}>{ruta.estatus || 'SIN ESTADO'}</span>

                            </div>
                            {
                                ruta.incidencia != 'SIN INCIDENCIAS'? (
                                    <div className="incidencias-container">
                                        {
                                            (() => {
                                                const incidencias = ruta.incidencia.split('|').map(inc => inc.trim());
                                                const latitudes = (ruta.latitud || ruta.lat_incidencia || '').toString().split('|').map(lat => lat.trim());
                                                const longitudes = (ruta.longitud || ruta.lon_incidencia || '').toString().split('|').map(lon => lon.trim());
                                                
                                                return incidencias.map((inc, idx) => {
                                                    const lat = latitudes[idx] || '20.5000';
                                                    const lon = longitudes[idx] || '-103.2520';
                                                    return (
                                                        <a 
                                                            key={idx}
                                                            href={`https://www.google.com/maps/search/?api=1&query=${lat},${lon}`} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <span className={`incidente`}>
                                                                {incidencias.length > 1 ? `${idx + 1}. ` : ''}{inc}
                                                            </span>
                                                        </a>
                                                    );
                                                });
                                            })()
                                        }
                                    </div>
                                ) : (
                                    <span className={`incidente no-incidente`}>{ruta.incidencia || 'N/A'}</span>
                                )
                            }

                        </div>
                    </div>
                ))}
            </div>

            {/* Footer con botón de copiar */}
            <div className="card-footer">
                <button
                    className={`btn-copiar ${claseZona}`}
                    onClick={handleCopyInfo}
                    title="Copiar información completa"
                >
                    📋 Mandar telegram
                </button>
            </div>
        </div>
    );
};

export default ClienteRutaCard;
